import { useMemo, ReactNode } from 'react';
import SearchIcon from '@material-ui/icons/Search';
import {
  Text,
  SearchField,
  Table,
  Cell,
  useTable,
  ColumnConfig,
  RowConfig,
  Skeleton,
} from '@backstage/ui';
import { ContentCard } from '../ContentCard/ContentCard';
import styles from './DataTableCard.module.css';

// Describes one column: its header label, and how to render a cell for a row.
export interface Column<T> {
  key: string;
  header: string;
  render: (row: T) => ReactNode;
  // Any CSS length ('192px', '2rem', '20%'); '%' and 'fr' (width only) pass through to react-aria.
  // The table scrolls horizontally when the columns are wider than the card.
  width?: string;
  minWidth?: string;
  maxWidth?: string;
}

// react-aria only takes px numbers, '%' or 'fr', so other CSS lengths are measured in the DOM.
// shortcut: measured once per `columns` change, so a later root font-size change isn't picked up.
function toColumnSize(
  value: string | undefined,
): number | `${number}%` | `${number}fr` | undefined {
  if (!value) return undefined;
  if (/^[\d.]+(%|fr)$/.test(value.trim()))
    return value.trim() as `${number}%` | `${number}fr`;
  const probe = document.createElement('div');
  probe.style.cssText = 'position:absolute;visibility:hidden;';
  probe.style.width = value;
  if (!probe.style.width) return undefined; // the browser rejected the value
  document.body.appendChild(probe);
  const px = probe.getBoundingClientRect().width;
  probe.remove();
  return px;
}

interface DataTableCardProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  searchPlaceholder?: string;
  // Which fields to match the search query against.
  searchMatch?: (row: T, query: string) => boolean;
  title?: string;
  action?: ReactNode;
  // Pagination is optional — only shown if pageSize is provided.
  pageSize?: number;
  onRowClick?: (row: T) => void;
  // Shows skeleton rows instead of the table while the rows are fetched.
  loading?: boolean;
}

// BUI's Table requires each row to carry an `id`; our rows don't, so each row
// is wrapped with the caller-provided rowKey as its id.
interface TableRow<T> {
  id: string;
  row: T;
}

export function DataTableCard<T>({
  columns,
  rows,
  rowKey,
  searchPlaceholder = 'Search...',
  searchMatch,
  title,
  action,
  pageSize,
  onRowClick,
  loading,
}: DataTableCardProps<T>) {
  const items = useMemo<TableRow<T>[]>(
    () => rows.map(row => ({ id: rowKey(row), row })),
    [rows, rowKey],
  );

  const columnConfig = useMemo<ColumnConfig<TableRow<T>>[]>(
    () =>
      columns.map((col, index) => ({
        id: col.key,
        label: col.header,
        cell: (item: TableRow<T>) => <Cell>{col.render(item.row)}</Cell>,
        // react-aria's Table requires exactly one row-header column.
        isRowHeader: index === 0,
        width: toColumnSize(col.width),
        // 'fr' is width-only; react-aria ignores it for min/max.
        minWidth: toColumnSize(col.minWidth) as ColumnConfig<
          TableRow<T>
        >['minWidth'],
        maxWidth: toColumnSize(col.maxWidth) as ColumnConfig<
          TableRow<T>
        >['maxWidth'],
      })),
    [columns],
  );

  const rowConfig: RowConfig<TableRow<T>> | undefined = onRowClick
    ? { onClick: item => onRowClick(item.row) }
    : undefined;

  const { tableProps, search } = useTable<TableRow<T>>({
    mode: 'complete',
    data: items,
    searchFn: searchMatch
      ? (data, query) => data.filter(item => searchMatch(item.row, query))
      : data => data,
    paginationOptions: pageSize ? { type: 'page', pageSize } : { type: 'none' },
  });

  return (
    <ContentCard>
      {(title || action) && (
        <div className={styles.header}>
          {title && (
            <Text as="h6" variant="title-small" className={styles.title}>
              {title}
            </Text>
          )}
          {action}
        </div>
      )}

      <div className={styles.searchWrapper}>
        <SearchField
          className={styles.search}
          placeholder={searchPlaceholder}
          icon={<SearchIcon fontSize="small" />}
          value={search.value}
          onChange={search.onChange}
        />
      </div>

      {loading ? (
        <div className={styles.skeletonRows}>
          {[1, 2, 3].map(i => (
            <Skeleton key={i} width="100%" height={32} />
          ))}
        </div>
      ) : (
        <Table
          {...tableProps}
          columnConfig={columnConfig}
          rowConfig={rowConfig}
        />
      )}
    </ContentCard>
  );
}
